<template>
  <div class="edit">
    <content-header/>
    <!-- svgが入ってる時にエラーが出ないように https://github.com/yansern/vue-multipane/issues/19 -->
    <multipane class="pane" layout="vertical">
      <!-- 左側：プレビュー -->
      <div @mousedown.stop class="preview-wrapper">
        <preview
          ref="preview"
          @showLoading="showLoading"
        />
        <div class="white-wrap" :class="{loading: isLoading}">
          <div class="spinner">
            <span>Loading...</span>
          </div>
        </div>
      </div>
      <multipane-resizer/>
      <!-- 右側：パレット -->
      <div @mousedown.stop class="palette-wrapper">
        <palette ref="palette"/>
      </div>
    </multipane>
  </div>
</template>

<script>
import ContentHeader from '@/components/Content/ContentHeader'
import Preview from '@/components/Content/Preview'
import Palette from '@/components/Content/Palette'
import { Multipane, MultipaneResizer } from 'vue-multipane'
import { httpPromises } from '@/utils/Http'

export default {
  head: {
    script: [
      { src: '/js/customTinymce/tinymce.min.js' }
    ],
    link: [
      { rel: 'stylesheet', href: '/style/content-default.css' },
    ],
  },
  components: {
    Preview,
    Palette,
    Multipane,
    MultipaneResizer,
    ContentHeader,
  },
  data () {
    return {
      isLoading: true,
      jsonStatus: null,
    }
  },
  mounted () {
    window.addEventListener('beforeunload', this.handleBeforeUnload)
  },
  beforeDestroy () {
    window.removeEventListener('beforeunload', this.handleBeforeUnload)
  },
  methods: {
    async handleBeforeUnload (event) {
      // タブを閉じた時や別のサイトのページに移動した時
      if (
        await Promise.allSettled(httpPromises) && // 応答待ち中に閉じるボタンを押下された時、編集差分ポップアップを表示できるよう、すべてのPromiseが解決or拒否されるまで待つ
        this.$utils.token.get() && // セッション切れた時のダイアログを優先させる
        this.$utils.string.hasEditDiff(this.$store.state)
      ) {
        const message = '編集内容が破棄されます\nページを離れますか？'
        event.returnValue = message
        if (await this.forceReloadCheck()) return
        return message
      }
    },
    async forceReloadCheck () {
      // メンテナンスやバージョンアップデート時、強制リロードが、ダイアログ表示で妨害されてしまうので
      // ダイアログ表示前に、Http.js内で強制リロードさせるように対応
      // メンテナンスやバージョンアップデートがなければ何もしない
      const json = await this.$http({
        url: '/status',
        type: 'GET',
      })
      return !json || json.status !== 200
    },
    async waitImages () {
      const previewRef = this.$refs.preview
      if (!previewRef) return
      const images = previewRef.$el.getElementsByTagName('img')
      // 画像を待つように
      const promises = []
      const loadImage = img => {
        return new Promise((resolve, reject) => {
          if (img.complete) resolve(img)
          img.addEventListener('load', () => resolve(img))
          img.addEventListener('error', () => resolve(img))
        })
      }
      images.forEach(image => promises.push(loadImage(image)))
      return Promise.all(promises)
    },
    showLoading (bool) {
      this.isLoading = bool
    },
  },
  async created () {
    const contentId = Number(this.$route.params.id)
    const json = await this.$store.dispatch('getEditContent', contentId)
    if (json && json.status === 200) {
      this.$nextTick(() => {
        this.waitImages().then(() => {
          this.isLoading = false
        })
      })
    } else {
      // どこかをクリックしたら一覧に遷移させる
      document.body.addEventListener('click', () => {
        this.$router.push({ name: 'contentList' })
      }, { once: true })
    }
    this.jsonStatus = json.status
  },
  async beforeRouteLeave (to, from, next) {
    // vue routerでページ離れる時
    if (
      this.jsonStatus === 200 &&
      this.$utils.token.get() && // セッション切れた時のダイアログを優先させる
      this.$utils.string.hasEditDiff(this.$store.state)
    ) {
      if (await this.forceReloadCheck()) return
      const answer = window.confirm('編集内容が破棄されます\nページを離れますか？')
      if (answer) {
        next()
      } else {
        return
      }
    }
    // store をクリア
    this.$store.commit('clearStateToLeaveContentEdit')
    next()
  },
}
</script>

<style lang="scss" scoped>
.edit {
  .pane {
    height: calc(100vh - #{$defaultTopMargin} - #{$editorNavbarHeight});
    .preview-wrapper {
      width: calc(100vw - #{$paletteWidth});
      position: relative;
      overflow-y: scroll;
      .white-wrap {
        position: absolute;
        top: 0;
        right: 0;
        width: 100%;
        height: 100%;
        background: #fff;
        opacity: 0;
        transition: all 0.5s ease 0s;
        visibility: hidden;
        &.loading {
          opacity: 1;
          visibility: visible;
        }
      }
    }
    .palette-wrapper {
      width: $paletteWidth;
      flex-grow: 1; // これを外すと、パレットのドラッグで幅変更が動かなくなるので必要
      overflow-y: hidden; // これを外すと、パレットの縦スクロールが効かなくなるので必要
      background: #F8F8F8;
      border-left: 0.5px solid #dbdbdb;
    }
  }
}
</style>
